<template>
  <div v-click-outside="onClickOutside" class="emh-input-autocomplete">
    <wb-input-control>
      <wb-input>
        <label>{{ label }}</label>
        <input
          v-model="searchRef"
          data-test-id="emh-input-autocomplete-input-search"
          type="text"
          @input="onSearchChange"
          @click="onFocus"
          @focus="onFocus"
          @keydown="onKeyDown"
        />
        <wb-input-action class="emh-input-autocomplete__controls">
          <transition name="emh-fade">
            <button
              v-if="search"
              class="wb-round-button wb-round-button--small"
              data-test-id="emh-input-autocomplete-controls-close"
              @click.stop.prevent="onReset"
            >
              <wb-icon
                class="wb-round-button__icon"
                aria-hidden="true"
                :flip-rtl.attr="true"
                :name.attr="'close'"
              ></wb-icon>
            </button>
          </transition>
        </wb-input-action>
      </wb-input>
    </wb-input-control>

    <div
      v-if="isFocused && (autosuggestions.length || (search && !loading))"
      class="emh-input-autocomplete__autosuggestions wb-list-group"
    >
      <button
        v-for="(autosuggestion, index) in autosuggestions"
        :key="autosuggestion.id"
        :class="{
          'emh-input-autocomplete__autosuggestion': true,
          'wb-list-group__item': true,
          '-active': index === currentIndex,
        }"
        class="emh-input-autocomplete__autosuggestion wb-list-group__item"
        type="button"
        @click="onSuggestionSelect(autosuggestion)"
        @mouseenter="onMouseEnter(index)"
      >
        {{ autosuggestion.label }}
      </button>
      <button
        v-if="search && !loading && !autosuggestions.length"
        class="emh-input-autocomplete__autosuggestion wb-list-group__item"
        disabled
        type="button"
      >
        {{ notFoundLabel }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch } from 'vue';
import { debounce } from '@workbench/core';
import { ClickOutsideDirective } from '../../../directives/clickOutside';

export interface Autosuggestion {
  id: string;
  label: string;
}

export type Autosuggestions = Autosuggestion[];

export default defineComponent({
  name: 'InputAutocomplete',

  directives: {
    clickOutside: ClickOutsideDirective,
  },

  props: {
    label: {
      type: String,
      required: true,
    },
    search: {
      type: String,
      default: '',
    },
    autosuggestions: {
      type: Array as PropType<Autosuggestions>,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    notFoundLabel: {
      type: String,
      required: true,
    },
  },

  emits: ['search-change', 'search-focus', 'suggestion-select', 'reset'],

  setup(props, { emit }) {
    const searchRef = ref('');
    const isFocused = ref(false);
    const currentIndex = ref(0);

    const emits = {
      searchChange: (search: string): void => emit('search-change', search),
      searchFocus: (): void => emit('search-focus'),
      suggestionSelect: (suggestion: any): void => emit('suggestion-select', suggestion),
      reset: (): void => emit('reset'),
    };

    /**
     * Emit a new search value with a debounce function
     */
    let onSearchChange = (): void => {
      if (!isFocused.value) {
        onFocus();
      }

      emits.searchChange(searchRef.value);
    };

    onSearchChange = debounce(onSearchChange, 300);

    /**
     * Emit a new selected suggestion and remove focus from element to hide autosuggestions
     */
    const onSuggestionSelect = (suggestion: Autosuggestion): void => {
      emits.suggestionSelect(suggestion);
      isFocused.value = false;
    };

    /**
     * Emit the focus event to load new suggestions if needed and reset selected index
     */
    const onFocus = (): void => {
      if (isFocused.value) return;

      emits.searchFocus();
      isFocused.value = true;
      currentIndex.value = 0;
    };

    /**
     * Remove focus from element if a click were outside of the element
     */
    const onClickOutside = (): void => {
      isFocused.value = false;
    };

    /**
     * Change an index on hover
     */
    const onMouseEnter = (index: number): void => {
      currentIndex.value = index;
    };

    /**
     * Emit the reset event to clear a state in a parent component or store
     */
    const onReset = (): void => emits.reset();

    /**
     * Here we select a suggestion depending on the current index,
     * which can be changed on hover or via keyboards "up" and "down"
     */
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!props.autosuggestions.length) {
        return;
      }

      switch (event.key) {
        case 'Enter':
          if (isFocused.value) {
            emits.suggestionSelect(props.autosuggestions[currentIndex.value]);
            isFocused.value = false;
          } else {
            onFocus();
          }

          break;
        case 'ArrowUp':
        case 'Up':
          event.preventDefault();
          goToSearchIndex(currentIndex.value - 1);
          break;
        case 'ArrowDown':
        case 'Down':
          event.preventDefault();
          goToSearchIndex(currentIndex.value + 1);
          break;
      }
    };

    /**
     * Changed currently selected index
     */
    const goToSearchIndex = (index: number): void => {
      // Move to the first one if index is bigger that number of items
      if (index >= props.autosuggestions.length) {
        currentIndex.value = 0;
      }
      // Move to the last one if index is negative
      else if (index < 0) {
        currentIndex.value = props.autosuggestions.length - 1;
      } else {
        currentIndex.value = index;
      }
    };

    /**
     * Change current input value if it was changed from outside
     */
    watch(
      () => props.search,
      (newValue: string) => {
        if (newValue !== searchRef.value) {
          searchRef.value = newValue;
        }
      },
      { immediate: true },
    );

    return {
      onSearchChange,
      onSuggestionSelect,
      onFocus,
      onClickOutside,
      onKeyDown,
      onMouseEnter,
      onReset,

      searchRef,
      isFocused,
      currentIndex,
    };
  },
});
</script>

<style lang="scss" src="./inputAutocomplete.scss"></style>
